import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import AlertTriangle from '../Icons/SVG/AlertTriangle';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  width: 100%;
`;
const StyledInput = styled.input`
  ${(props) => props.theme.typography.bodyThin};
  background: ${(props) => props.theme.colors.grayscale.white};
  border: 1px solid ${(props) => props.theme.colors.grayscale.gray2};
  color: ${(props) => props.theme.colors.grayscale.black};
  height: 2.188em;
  padding: 0.625rem 0;
  position: relative;
  text-indent: 1rem;
  width: 100%;

  &:invalid {
    border: 1px solid ${(props) => props.theme.colors.red.primary};
  }
`;

const StyledLabel = styled.label`
  ${(props) => props.theme.typography.bodyThin};
  background: ${(props) => props.theme.colors.grayscale.white};
  color: ${(props) => props.theme.colors.grayscale.gray1};
  cursor: text;
  left: 0.8rem;
  position: absolute;
  transition: all 0.28s ease;

  &.not-floating {
    top: 1.125rem;
  }

  &.floated {
    display: none;
  }

  &.error {
    color: ${(props) => props.theme.colors.red.primary};
  }
`;

const StyledMessage = styled.div`
  ${(props) => props.theme.typography.bodySmall};
  line-height: 1rem;
  padding-left: 0.875rem;
  margin-top: 0.5rem;
  text-align: left;

  &.error-msg {
    color: ${(props) => props.theme.colors.red.primary};
  }

  &.success-msg {
    color: #1ca01c;
  }
`;

const StyledErrorIcon = styled.div`
  bottom: 2.3rem;
  float: right;
  margin-right: 1em;
  position: absolute;
  right: 0;
  text-align: right;

  svg {
    fill: ${(props) => props.theme.colors.red.primary};
    position: relative;
  }
`;

/**
 * This input takes the type of
 * tel, email, password and text. The component has no internal state, and follows a controlled pattern.
 * Tel type can only simulated on a mobile device and will default to text if not supported.
 */

function Input(props) {
  const {
    autoComplete,
    id,
    name,
    placeholder,
    onChangeHandler,
    errorMessage,
    successMessage,
    maxLength,
    required,
    setError,
    tabIndex,
    type,
    value,
    ...res
  } = props;

  const [floating, setFloatingLabel] = useState(false);

  const handleLabel = (e) => {
    switch (e.type) {
      case 'focus':
        setFloatingLabel(true);
        break;
      case 'blur':
        if (e.target.value === '') {
          setFloatingLabel(false);
        } else {
          setFloatingLabel(true);
        }
        break;
      // no default
    }
  };

  // removed floating logic because it's broken and unused by current designs
  const handleFloating = () => {
    if (errorMessage !== '') {
      return 'error floated';
    }
    if (!floating) {
      return 'not-floating';
    }
    return 'floated';
  };

  return (
    <InputContainer>
      <StyledInput
        aria-label={name}
        autoComplete={autoComplete}
        aria-required={required}
        id={id}
        maxlength={maxLength}
        name={name}
        onFocus={handleLabel}
        onBlur={handleLabel}
        onChange={onChangeHandler}
        tabIndex={tabIndex}
        type={type}
        value={value}
        {...res}
      />

      {errorMessage !== '' ? (
        <>
          <StyledErrorIcon>
            <AlertTriangle />
          </StyledErrorIcon>
          <StyledMessage className="error-msg">{errorMessage}</StyledMessage>
        </>
      ) : (
        false
      )}

      {successMessage !== '' ? (
        <StyledMessage className="success-msg">{successMessage}</StyledMessage>
      ) : (
        false
      )}
      <StyledLabel htmlFor={id} className={handleFloating()}>
        {placeholder}
      </StyledLabel>
    </InputContainer>
  );
}

/**
 * propTypes for this component
 */
Input.propTypes = {
  /** Type attribute for the input, which can only be password, text, email, and tel */
  type: PropTypes.oneOf(['email', 'text', 'tel', 'password']).isRequired,
  /** Name attribute for the input */
  name: PropTypes.string.isRequired,
  /** Id attribute for the input */
  id: PropTypes.string,
  /** value passed down from the parent */
  value: PropTypes.string,
  /** Placeholder for the input */
  placeholder: PropTypes.string,
  /** autoComplete passed down from the parent */
  autoComplete: PropTypes.string,
  /** maxLength passed down from the parent */
  maxLength: PropTypes.number,
  /** tabIndex passed down from the parent */
  tabIndex: PropTypes.number,
  /** errorMessage passed down from the parent */
  errorMessage: PropTypes.string,
  /** successMessage passed down from the parent */
  successMessage: PropTypes.string,
  /** required passed down from the parent */
  required: PropTypes.bool,
  /** onChangeHandler passed down from parent */
  onChangeHandler: PropTypes.func.isRequired,
  /** setError passed down from parent */
  setError: PropTypes.func,
};

Input.defaultProps = {
  id: '',
  value: '',
  placeholder: '',
  tabIndex: 0,
  required: false,
  autoComplete: 'off',
  setError: undefined,
  maxLength: 100,
  errorMessage: '',
  successMessage: '',
};

export default Input;
